/**
 * Treatment Detail Page - Full Content Parity with Admin CMS
 * All content rendered from TreatmentManagementContext
 * No hardcoded text - everything editable by TreatX Admin
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTreatmentManagement } from '../context/TreatmentManagementContext';
import { isTreatxAdmin } from '../utils/unifiedAuth';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft, Clock, DollarSign, MapPin, Star, CheckCircle, AlertTriangle,
  Calendar, Shield, Users, Building2, ChevronDown, ChevronUp, HelpCircle,
  Globe, Heart, Edit2
} from 'lucide-react';
import api from '../utils/api';
import SEO from '../components/SEO';


const TreatmentDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdminViewer = isTreatxAdmin();
  // TREATX-SEO-RECOVERY-S3A.1 — the three states a slug can be in are read from
  // the canonical context, not inferred: `loaded` (has the fetch settled) and
  // `catalogError` (did it actually fail). An empty catalogue that loaded
  // SUCCESSFULLY is a valid answer, and a slug absent from it is a genuine 404
  // — guessing "empty means broken" was the S3A defect this corrects.
  const {
    getTreatmentBySlug,
    loaded: catalogLoaded,
    catalogError,
    fetchTreatments,
  } = useTreatmentManagement();
  
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [discoveryClinics, setDiscoveryClinics] = useState([]);
  const [clinicsLoading, setClinicsLoading] = useState(false);

  // Compute treatment data from context using useMemo to avoid setState in useEffect
  const { treatment, loading } = React.useMemo(() => {
    // Get treatment from context
    const contextTreatment = getTreatmentBySlug(slug);
    
    if (contextTreatment) {
      // Transform context data to page format
      // P1-2: Price source-of-truth fix — always derive from priceMode + manualPrice fields
      let priceMin, priceMax;
      if (contextTreatment.priceMode === 'manual' && (contextTreatment.manualPriceMin || contextTreatment.manualPriceMax)) {
        priceMin = contextTreatment.manualPriceMin || 0;
        priceMax = contextTreatment.manualPriceMax || 0;
      } else if (contextTreatment.calculatedPriceMin || contextTreatment.calculatedPriceMax) {
        priceMin = contextTreatment.calculatedPriceMin || 0;
        priceMax = contextTreatment.calculatedPriceMax || 0;
      } else {
        priceMin = contextTreatment.priceRange?.min || 0;
        priceMax = contextTreatment.priceRange?.max || 0;
      }
      return {
        treatment: {
          ...contextTreatment,
          priceRange: {
            min: priceMin,
            max: priceMax,
            currency: contextTreatment.priceRange?.currency || 'EUR',
            note: contextTreatment.priceRange?.note || ''
          }
        },
        loading: false
      };
    }
    
    // Fallback: check for similar slugs in active treatments
    //
    // TREATX-SEO-RECOVERY-S3A — the fuzzy fallback that used to live here
    // (`t.slug.includes(slug) || slug.includes(t.slug.split('-')[0])`) resolved
    // INVALID slugs to a real treatment and then rendered it under the invalid
    // URL with a self-referencing canonical — i.e. it manufactured indexable
    // duplicate pages for every typo and every stale link. Only an exact slug
    // match (handled above) identifies a treatment now; anything else is a
    // genuine Not Found.
    return { treatment: null, loading: false };
  }, [slug, getTreatmentBySlug]);

  // P6 FOUNDATION — SINGLE SEARCH SOURCE OF TRUTH
  // Use /search/providers (provider_compliance_summary) instead of /discover/clinics.
  useEffect(() => {
    if (!treatment) return;
    let cancelled = false;
    setClinicsLoading(true);
    api.get(`/search/providers?treatment=${encodeURIComponent(slug)}&limit=6`)
      .then(({ data }) => data).catch(() => (Promise.reject()))
      .then(data => {
        if (cancelled) return;
        const providers = (data && data.success && Array.isArray(data.providers)) ? data.providers : [];
        // TCPN-MP0-F cutover — the "Verified" badge on the discovery
        // mini-card derives from the canonical 4-condition accreditation
        // invariant. Legacy `verification.overall_status` is no longer
        // consulted as a trust signal.
        const mapped = providers.map(p => {
          const canonicalVerified = Array.isArray(p.accreditations)
            ? p.accreditations.some(a =>
                a
                && a.slug
                && a.is_verified_new === true
                && a.is_self_attested !== true
                && a.evidence
                && a.evidence.doc_id
              )
            : false;
          return {
            clinic_id: p.clinic_id || p.provider_id,
            name: p.clinic_name || (p.location?.city ? `Clinic in ${p.location.city}` : `Provider ${(p.clinic_id || '').slice(-8)}`),
            city: p.location?.city || '',
            country: p.location?.country || '',
            trust_badges: {
              verified: canonicalVerified,
            },
          };
        });
        setDiscoveryClinics(mapped);
      })
      .catch(() => { if (!cancelled) setDiscoveryClinics([]); })
      .finally(() => { if (!cancelled) setClinicsLoading(false); });
    return () => { cancelled = true; };
  }, [slug, treatment]);

  const clinicCount = discoveryClinics.length;

  // ── SEO: Dynamic metadata via react-helmet-async ──────────────
  // PUBLIC-SEO-LAUNCH-GAP-REMEDIATION (2026-02): the imperative
  // document.head manipulation that lived here has been replaced by
  // the shared <SEO> component (see components/SEO.jsx). All values
  // still derive from the canonical Treatment SSOT (treatment_content
  // via TreatmentManagementContext); no parallel SEO CMS was
  // introduced.
  const seoBlock = React.useMemo(() => {
    if (!treatment) return null;
    const title = treatment.seoTitle || treatment.name;
    const desc = treatment.seoDescription || treatment.shortDescription || '';
    const image = treatment.seoImage || treatment.image || '';
    const keywords = treatment.seoKeywords || '';
    const canonicalPath = `/treatments/${slug}`;
    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'MedicalProcedure',
      name: treatment.name,
      description: treatment.shortDescription || '',
      procedureType: treatment.category || '',
      bodyLocation: treatment.category || '',
      howPerformed: treatment.description?.overview || '',
      preparation: treatment.procedureDuration
        ? `Procedure duration: ${treatment.procedureDuration}`
        : undefined,
      followup: treatment.recoveryTime
        ? `Recovery time: ${treatment.recoveryTime}`
        : undefined,
      estimatedCost: treatment.priceRange
        ? {
            '@type': 'MonetaryAmount',
            currency: treatment.priceRange.currency || 'EUR',
            minValue: treatment.priceRange.min || 0,
            maxValue: treatment.priceRange.max || 0,
          }
        : undefined,
    };
    Object.keys(jsonLd).forEach((k) => jsonLd[k] === undefined && delete jsonLd[k]);
    return (
      <SEO
        title={title}
        description={desc}
        canonicalPath={canonicalPath}
        image={image}
        imageAlt={treatment.name}
        keywords={keywords}
        type="article"
        jsonLd={jsonLd}
      />
    );
  }, [treatment, slug]);

  const handleGetQuote = () => {
    // P1-010: Store treatment intent in localStorage (survives page refresh & navigation)
    const intent = {
      treatment_id: treatment.slug || treatment.id,
      treatment_name: treatment.name,
      timestamp: Date.now(),
    };
    localStorage.setItem('treatx_treatment_intent', JSON.stringify(intent));
    
    if (user) {
      navigate(`/patient/intake-enhanced?treatment=${treatment.slug}`);
    } else {
      // Also keep sessionStorage for legacy redirect
      sessionStorage.setItem('preselectedTreatment', treatment.name);
      sessionStorage.setItem('preselectedTreatmentId', treatment.slug);
      sessionStorage.setItem('redirectAfterLogin', `/patient/intake-enhanced?treatment=${treatment.slug}`);
      navigate('/login');
    }
  };

  const handleFindClinics = () => {
    // P6 FOUNDATION — Use canonical /search with slug (not display name)
    // so the canonical search receives a slug it can actually filter on.
    const params = new URLSearchParams();
    if (treatment.slug) params.set('treatment', treatment.slug);
    if (treatment.slug) params.set('treatmentId', treatment.slug);

    // Get any saved intake data for additional filtering
    const savedIntake = sessionStorage.getItem('patientFlowState');
    if (savedIntake) {
      try {
        const intakeData = JSON.parse(savedIntake);
        if (intakeData.selectedBudget) params.set('budget', intakeData.selectedBudget);
      } catch (e) {
        // Ignore parse errors
      }
    }

    navigate(`/search-v2?${params.toString()}`);
  };

  if (loading || !catalogLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // TREATX-SEO-RECOVERY-S3A.1 — the catalogue request FAILED (both the canonical
  // endpoint and its fallback). "Absent" here means "could not be looked up",
  // never "does not exist", so this must not become a 404: answering a transient
  // outage with a permanent status would delete healthy URLs from the index.
  // A successful-but-empty catalogue deliberately does NOT reach this branch.
  if (!treatment && catalogError) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center p-8"
        data-testid="treatment-temporarily-unavailable"
      >
        <SEO
          title="Treatment Temporarily Unavailable"
          description="This treatment page cannot be loaded right now. Please try again shortly."
          noindex
          statusCode={503}
        />
        <h1 className="text-2xl font-bold mb-4">Temporarily unavailable</h1>
        <p className="text-gray-600 mb-6">
          We couldn&apos;t load the treatment catalogue just now. Please try again shortly.
        </p>
        <Button onClick={() => fetchTreatments()} data-testid="treatment-unavailable-retry">
          Try again
        </Button>
      </div>
    );
  }

  if (!treatment) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-8">
        {/* PUBLIC-SEO-LAUNCH-GAP-REMEDIATION: unknown treatment slugs
            render as a real Not Found UI with robots=noindex,follow so
            search engines drop the URL from their index. The canonical
            API `/api/public/treatments/{slug}` already returns HTTP 404
            (see backend/routes/treatment_content_routes.py). */}
        <SEO
          title="Treatment Not Found"
          description="The treatment you are looking for does not exist or has been removed."
          noindex
          statusCode={404}
        />
        <h1 className="text-2xl font-bold mb-4" data-testid="treatment-not-found-heading">Treatment Not Found</h1>
        <p className="text-gray-600 mb-6">The treatment you are looking for does not exist or has been removed.</p>
        <Button onClick={() => navigate('/treatments')} data-testid="treatment-not-found-cta">
          <ArrowLeft className="mr-2 h-4 w-4" />
          View All Treatments
        </Button>
      </div>
    );
  }

  // Extract content from treatment
  const overview = treatment.description?.overview || treatment.longDescription || treatment.shortDescription || '';
  const benefits = treatment.description?.benefits || [];
  const risks = treatment.description?.risks || [];
  const expectations = treatment.description?.expectations || '';
  const whoIsThisFor = treatment.whoIsThisFor || [];
  const whyAbroad = treatment.whyAbroad || [];
  const faqs = treatment.faqs || [];

  return (
    <div className="min-h-screen bg-gray-50">
      {seoBlock}
      {/* Hero Section */}
      <div className="relative h-[400px] bg-gradient-to-r from-blue-900 to-purple-900">
        <img
          src={treatment.image}
          alt={treatment.name}
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        
        <div className="relative max-w-7xl mx-auto px-4 h-full flex flex-col justify-end pb-12">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="absolute top-4 left-4 text-white hover:bg-white/20"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>

          <Badge className="w-fit mb-4 bg-blue-600">{treatment.category}</Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {treatment.name}
          </h1>
          <p className="text-xl text-gray-200 max-w-2xl">
            {treatment.shortDescription || overview.substring(0, 150)}
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Overview Section */}
            {overview && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5 text-blue-600" />
                    Overview
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                    {overview}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Benefits Section */}
            {benefits.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    Benefits
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {benefits.map((benefit, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Who Is This For Section */}
            {whoIsThisFor.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Heart className="h-5 w-5 text-pink-600" />
                    Who Is This For?
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {whoIsThisFor.map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <Users className="h-5 w-5 text-pink-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* What to Expect Section */}
            {expectations && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-blue-600" />
                    What to Expect
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                    {expectations}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Risks & Considerations Section */}
            {risks.length > 0 && (
              <Card className="border-orange-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-orange-600" />
                    Potential Risks & Considerations
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-orange-800">
                      All medical procedures carry some risk. Our partner clinics will discuss these with you during consultation.
                    </p>
                  </div>
                  <ul className="space-y-3">
                    {risks.map((risk, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <AlertTriangle className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{risk}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Why Choose This Treatment Abroad */}
            {whyAbroad.length > 0 && (
              <Card className="bg-gradient-to-br from-blue-50 to-purple-50 border-blue-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="h-5 w-5 text-blue-600" />
                    Why Patients Choose This Treatment Abroad
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {whyAbroad.map((reason, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <Star className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{reason}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* FAQ Section */}
            {faqs.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <HelpCircle className="h-5 w-5 text-purple-600" />
                    Frequently Asked Questions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {faqs.map((faq, index) => (
                      <div 
                        key={index} 
                        className="border rounded-lg overflow-hidden"
                      >
                        <button
                          onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                          className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
                        >
                          <span className="font-medium text-gray-900">{faq.question}</span>
                          {expandedFaq === index ? (
                            <ChevronUp className="h-5 w-5 text-gray-500" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-gray-500" />
                          )}
                        </button>
                        {expandedFaq === index && (
                          <div className="px-4 pb-4 text-gray-600 border-t bg-gray-50">
                            <p className="pt-4">{faq.answer}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Top Clinics for This Treatment — conditional on discovery results */}
            {!clinicsLoading && discoveryClinics.length > 0 && (
              <Card data-testid="treatment-clinics-section">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-blue-600" />
                    Top Clinics for {treatment.name}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {discoveryClinics.map((clinic) => (
                      <div
                        key={clinic.clinic_id}
                        className="flex items-center justify-between p-3 border rounded-lg hover:border-blue-200 hover:bg-blue-50/30 transition-colors cursor-pointer"
                        data-testid={`treatment-clinic-${clinic.clinic_id}`}
                        onClick={() => navigate(`/clinics/${clinic.clinic_id}`)}
                      >
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-gray-900 truncate">{clinic.name}</p>
                          <div className="flex items-center gap-1 mt-0.5">
                            <MapPin className="h-3 w-3 text-gray-400 flex-shrink-0" />
                            <span className="text-xs text-gray-500 truncate">
                              {[clinic.city, clinic.country].filter(Boolean).join(', ')}
                            </span>
                          </div>
                          {clinic.trust_badges?.verified && (
                            <div className="flex items-center gap-1 mt-1">
                              <Shield className="h-3 w-3 text-emerald-500" />
                              <span className="text-[10px] text-emerald-600 font-medium">Verified</span>
                            </div>
                          )}
                        </div>
                        <ArrowLeft className="h-4 w-4 text-gray-300 rotate-180 flex-shrink-0" />
                      </div>
                    ))}
                  </div>
                  {clinicCount > 3 && (
                    <Button
                      variant="outline"
                      className="w-full mt-4"
                      onClick={handleFindClinics}
                      data-testid="view-all-clinics-btn"
                    >
                      View All {clinicCount} Clinics
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Quick Info Card */}
            <Card className="sticky top-6">
              <CardHeader>
                <CardTitle className="text-center">Quick Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Dynamic Marketing Line */}
                {clinicCount > 0 && (
                  <div className="flex items-center gap-2 p-3 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-100">
                    <Building2 className="h-5 w-5 text-blue-600 flex-shrink-0" />
                    <p className="text-sm font-medium text-blue-800">
                      Compare and choose among <span className="font-bold text-purple-700">{clinicCount}</span> TreatX verified clinics offering this treatment
                    </p>
                  </div>
                )}

                {/* Price */}
                <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-green-600" />
                    <span className="font-medium">Price Range</span>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-green-700">
                      {treatment.priceRange?.currency || '€'}{(treatment.priceRange?.min || 0).toLocaleString()} - {treatment.priceRange?.currency || '€'}{(treatment.priceRange?.max || 0).toLocaleString()}
                    </div>
                    {treatment.priceRange?.note && (
                      <div className="text-xs text-gray-600">{treatment.priceRange.note}</div>
                    )}
                  </div>
                </div>

                {/* Procedure Duration */}
                {treatment.procedureDuration && (
                  <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Clock className="h-5 w-5 text-purple-600" />
                      <span className="font-medium">Procedure Time</span>
                    </div>
                    <span className="font-bold text-purple-700">{treatment.procedureDuration}</span>
                  </div>
                )}

                {/* Recovery Time */}
                {treatment.recoveryTime && (
                  <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Clock className="h-5 w-5 text-blue-600" />
                      <span className="font-medium">Recovery Time</span>
                    </div>
                    <span className="font-bold text-blue-700">{treatment.recoveryTime}</span>
                  </div>
                )}

                {/* Recommended Stay */}
                {treatment.stayDuration && (
                  <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-5 w-5 text-amber-600" />
                      <span className="font-medium">Recommended Stay</span>
                    </div>
                    <span className="font-bold text-amber-700">{treatment.stayDuration}</span>
                  </div>
                )}

                {/* Category */}
                <div className="text-center pt-4 border-t">
                  <Badge variant="outline" className="text-sm capitalize">
                    {treatment.category}
                  </Badge>
                </div>

                {/* CTA Buttons */}
                {isAdminViewer ? (
                  <>
                    <div
                      className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
                      data-testid="admin-cta-neutral"
                    >
                      You are signed in as a TreatX admin — patient call-to-action buttons are hidden for this preview.
                    </div>
                    <Button
                      size="lg"
                      variant="outline"
                      className="w-full"
                      onClick={() => navigate('/treatx-admin/treatments')}
                      data-testid="admin-open-editor-btn"
                    >
                      <Edit2 className="mr-2 h-4 w-4" />
                      Open Treatment Editor
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      size="lg"
                      className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-lg py-6"
                      onClick={handleGetQuote}
                      data-testid="treatment-get-quote-btn"
                    >
                      Get Your Personalized Offer
                    </Button>

                    {clinicCount > 0 && (
                      <Button
                        size="lg"
                        variant="outline"
                        className="w-full"
                        onClick={handleFindClinics}
                        data-testid="treatment-browse-clinics-btn"
                      >
                        <Building2 className="mr-2 h-4 w-4" />
                        Browse {clinicCount} Clinics
                      </Button>
                    )}

                    <p className="text-xs text-center text-gray-500">
                      {user ? 'Continue to personalize your offer' : 'Login required to request an offer'}
                    </p>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Trust Badges */}
            <Card>
              <CardHeader>
                <CardTitle className="text-center flex items-center justify-center gap-2">
                  <Shield className="h-5 w-5 text-blue-600" />
                  Your Safety Matters
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-gray-600">
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
                  <span>Verified partner clinics only</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
                  <span>Board-certified surgeons</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
                  <span>Transparent pricing</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
                  <span>24/7 support throughout your journey</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TreatmentDetailPage;
